/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { catalogContainerStyles } from "@rc/App/SharedStyles/catalogStyles";
import { xProductListState } from "@rc/App/Store/Reducers";
import { color } from "@rc/XDL/Utilities";
import React, { FC } from "react";
import { connect, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState & xProductListState) => ({
    isFiltersDrawerOpen: state.xProductList.isFiltersDrawerOpen,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface ProductListColumnsStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnTableViewGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnTableViewGridItem?: GridItemProps;

    // xng-change
    filtersDrawer?: DrawerPresentationProps;
}

export const columnsStyles: ProductListColumnsStyles = {
    filtersDrawer: {
        position: "left",
        size: 360,
        cssOverrides: {
            scrim: css`
                [aria-labelledby="drawerTitle"] {
                    background-color: ${color("common.white")};
                    padding: 0 16px;
                    @media (min-width: 1025px) {
                        top: 110px;
                    }
                    @media (max-width: 1024px) {
                        top: 60px;
                    }

                    @media (max-width: 480px) {
                        width: 100%;
                    }
                }
            `,
        },
    },
};

const styles = columnsStyles;

const ProductListColumns: FC<Props> = ({ id, isFiltersDrawerOpen }) => {
    const dispatch = useDispatch();

    return (
        <StyledWrapper>
            <StyledWrapper {...catalogContainerStyles.container}>
                <StyledWrapper {...catalogContainerStyles.leftColumn}>
                    <Zone contentId={id} zoneName="Content00" />
                </StyledWrapper>
                <StyledWrapper {...catalogContainerStyles.leftColumn} className="mobile-only">
                    <Drawer
                        headline="Close Filters"
                        isOpen={isFiltersDrawerOpen}
                        handleClose={() =>
                            dispatch({ type: "xProductList/isFiltersDrawerOpen", isFiltersDrawerOpen: false })
                        }
                        {...styles.filtersDrawer}
                    >
                        <StyledWrapper className="left-col">
                            <Zone contentId={id} zoneName="Content00" />
                        </StyledWrapper>
                    </Drawer>
                </StyledWrapper>
                <StyledWrapper {...catalogContainerStyles.rightColumn}>
                    <Zone contentId={id} zoneName="Content01" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListColumns),
    definition: {
        group: "Product List",
        displayName: "Columns",
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
